/**
 * Display helpers shared by the report's cards.
 */

/**
 * Some stored names were assembled from missing parts ("Khadir undefined").
 * Drop the placeholder words rather than print them; the stored data still
 * needs correcting at source.
 */
export const cleanName = (name?: string | null) =>
  (name || "")
    .replace(/\b(undefined|null)\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim() || "Unnamed";

/** Route segments as people would say them. */
const SEGMENT_LABELS: Record<string, string> = {
  contractors: "contractor",
  contracts: "contract",
  ltas: "LTA",
  persons: "person",
  "psea-assessments": "PSEA assessment",
  "risk-assessments": "risk assessment",
  "capacity-assessments": "capacity assessment",
  pvrs: "partner verification report",
  "partner-verification-reports": "partner verification report",
  assessments: "assessment",
  "performance-ratings": "performance rating",
  agencies: "agency",
  users: "user",
  merge: "merge",
};

const ACTIONS: Record<string, string> = {
  edit: "Edit",
  new: "New",
  create: "New",
  merge: "Merge",
};

const capitalise = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

/**
 * Pages recorded without a name arrive as a route pattern, with record ids
 * collapsed to ":id" ("/contractors/:id", "/assessments/psea-assessments/:id/edit").
 * Turn those into plain names ("Contractor details", "Edit PSEA assessment");
 * anything that is already a name passes through unchanged.
 */
export const pageLabel = (name: string) => {
  if (!name.startsWith("/")) return name;

  const segments = name.split("/").filter(Boolean);
  const hasId = segments.includes(":id");
  const action = segments.map((s) => ACTIONS[s]).find(Boolean);
  const nouns = segments.filter((s) => s !== ":id" && !ACTIONS[s]);
  const last = nouns[nouns.length - 1];
  if (!last) return "Home";

  const noun =
    SEGMENT_LABELS[last] ||
    last.replace(/-/g, " ").replace(/s$/, hasId ? "" : "s");

  if (action) return `${action} ${noun}`;
  if (hasId) return `${capitalise(noun)} details`;
  return capitalise(SEGMENT_LABELS[last] ? `${noun}s` : noun);
};

/** Trend bucket keys come back as YYYY-MM-DD, YYYY-MM or YYYY. */
export const formatBucket = (bucket: string, granularity: string) => {
  if (granularity === "year") return bucket;

  const date =
    granularity === "month"
      ? new Date(`${bucket}-01T00:00:00Z`)
      : new Date(`${bucket}T00:00:00Z`);

  if (isNaN(date.getTime())) return bucket;

  return granularity === "month"
    ? date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
};

/**
 * A theme colour read at render time. The shared chart-colour hook looks the
 * chart up once on mount, before the report's charts exist, so it never
 * resolves and Apex falls back to its own palette.
 */
export const themeColor = (name: string, fallback: string) => {
  try {
    return (
      getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim() || fallback
    );
  } catch {
    return fallback;
  }
};

/** Axis titles and labels sized to be read, not squinted at. */
export const AXIS_TITLE_STYLE = { fontSize: "13px", fontWeight: 500 };
export const AXIS_LABEL_STYLE = { fontSize: "12px" };
